import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { Badge } from "../badge";
import { Heading } from "../heading";
import { Button } from "../button";
import {
  nombreCliente,
  contactoCliente,
  resumenCliente,
  eventosSinLigarQueCoinciden,
  actualizarCliente,
  contactosDeCliente,
  patchContactos,
  nombreContacto,
  nuevoIdContacto,
  archivarCliente,
  ligarEventoACliente,
  eventosDondeParticipa,
} from "../../utils/clientes";
import { ClienteFormFields, FORM_CLIENTE_VACIO } from "./ClienteCreateDialog";
import ClienteEventoDetalle from "./ClienteEventoDetalle";
import { formatDiaEvento } from "../../utils/dates";
import { getValorEvento } from "../../utils/valorEvento";
import { esSoloLectura } from "../../utils/roles";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
  UserIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  LinkIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const CONTACTO_VACIO = {
  nombre: "",
  apellido: "",
  puesto: "",
  correo: "",
  telefono: "",
};
const fmtMoney = (v) => "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });
const fmtDate = (ts, tz) => {
  const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
  if (!d || isNaN(d)) return "—";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: tz });
};
// "lun 8 dic 2026": en este negocio el día de la semana es medio dato del
// evento (un sábado no es un martes), y aquí se lee de corrido.
// startTime es un instante: sin el heurístico legacy (18:00 CDMX = 00:00 UTC).
const fmtDia = (ts, tz) => formatDiaEvento(ts, tz, { instante: true }) || "—";

const Card = ({ title, action, children }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">{title}</div>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

// Ficha de cliente (FRA-354): quién es, cómo contactarlo, su historial de
// eventos con valor, pagado y deuda; ligar eventos viejos que coinciden; y
// generar un lead nuevo con sus datos ya puestos (el cliente recurrente que
// pregunta por otra fecha).

// Un lead está en UNA columna: los nombres de sistema van en singular
// («Ganado», no «Ganados» como la columna). Las columnas propias del venue
// usan el nombre que les puso; sin él, la clave legible.
const ESTADOS_LEAD = {
  pendiente: "Pendiente",
  en_atencion: "En atención",
  completado: "Completado",
  ganado: "Ganado",
  perdido: "Perdido",
};
function etiquetaEstadoLead(estado, columnas = []) {
  if (ESTADOS_LEAD[estado]) return ESTADOS_LEAD[estado];
  const col = (columnas || []).find((c) => c?.key === estado);
  if (col?.label) return col.label;
  const legible = String(estado || "").replace(/_/g, " ").trim();
  return legible ? legible.charAt(0).toUpperCase() + legible.slice(1) : "Pendiente";
}

const ClientePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedVenueId, clientes, clientesLoading, events, payments, costos, proveedores, calendarTimezone, contactForms } =
    useVenue();
  const { userInfo, user } = useAuth();
  // Nombres de las columnas del pipeline de leads, para decir «En atención»
  // y no la clave «en_atencion». Las columnas propias del venue viven en
  // settings/leads; si no se pueden leer, quedan los nombres de sistema.
  const [columnasLeads, setColumnasLeads] = useState([]);
  useEffect(() => {
    if (!selectedVenueId) return undefined;
    let vivo = true;
    getDoc(doc(db, "venues", selectedVenueId, "settings", "leads"))
      .then((snap) => {
        if (vivo) setColumnasLeads(snap.exists() ? snap.data().columns || [] : []);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [selectedVenueId]);
  // Visor e inversionista consultan la ficha completa sin modificarla
  // (P-FRA-13): ni editar, ni archivar, ni contactos, ni ligar eventos.
  const soloLectura = esSoloLectura(userInfo);

  // De dónde viniste manda sobre el destino del regreso: entrar a la ficha
  // desde un evento y salir al Directorio te deja lejos de lo que estabas
  // haciendo. Mismo patrón que ProveedorPage.
  const backTarget = (() => {
    if (location.state?.from === "evento" && location.state?.eventId) {
      const eventTab = location.state.eventTab || "general";
      return {
        label: "Volver al evento",
        to: `/dashboard/eventos/${location.state.eventId}?tab=${encodeURIComponent(eventTab)}`,
      };
    }
    if (location.state?.from === "reportes") {
      return {
        label: "Volver al reporte",
        to: `/dashboard/reportes?r=${encodeURIComponent(location.state.reporte || "clientes")}`,
      };
    }
    return { label: "Directorio", to: "/dashboard/directorio?tipo=clientes" };
  })();
  const [nuevoContacto, setNuevoContacto] = useState(null);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(FORM_CLIENTE_VACIO);
  const [saving, setSaving] = useState(false);
  // Uno abierto a la vez: el detalle es alto y dos abiertos vuelven la
  // tabla un muro. Se guarda el id, no un set.
  const [eventoAbierto, setEventoAbierto] = useState(null);

  const cliente = useMemo(() => (clientes || []).find((c) => c.id === id) || null, [clientes, id]);
  const resumen = useMemo(
    () => resumenCliente({ cliente, events, payments, costos }),
    [cliente, events, payments, costos]
  );
  const sinLigar = useMemo(() => eventosSinLigarQueCoinciden(cliente, events), [cliente, events]);
  // Eventos donde participa sin ser el cliente principal (novia, quien paga,
  // productora…): se listan con su rol, sin dinero — el valor del evento ya
  // se contó en el principal.
  const participa = useMemo(() => eventosDondeParticipa(cliente, events), [cliente, events]);

  // Sus leads: cuántas veces ha cotizado, en qué quedó cada uno y cuáles se
  // convirtieron. Los del homesite ya nacen ligados (Flask) y los manuales
  // también; los viejos se ligan desde el lead.
  const leadsDelCliente = useMemo(() => {
    if (!cliente?.id) return [];
    const aMs = (v) => (v?.toDate ? v.toDate().getTime() : v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() : 0);
    return (contactForms || [])
      .filter((f) => f && !f.deleted && f.clienteId === cliente.id)
      .sort((a, b) => aMs(b.createdAt) - aMs(a.createdAt));
  }, [contactForms, cliente]);

  // La tabla de eventos mezcla los propios (con dinero) y aquellos donde
  // participa (con su rol, sin dinero), ordenados por fecha.
  const filasEventos = useMemo(() => {
    const aMs = (v) => (v?.toDate ? v.toDate().getTime() : v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() : 0);
    const propios = resumen.eventos.map((e) => ({ ...e, participa: false }));
    const otros = participa.map(({ event: e, rol }) => ({
      eventId: e.id,
      nombre: e.nombre || "Evento",
      startTime: e.startTime,
      cancelado: e.status === "cancelado",
      valor: getValorEvento(e) || 0,
      rol,
      participa: true,
    }));
    return [...propios, ...otros].sort((a, b) => aMs(b.startTime) - aMs(a.startTime));
  }, [resumen.eventos, participa]);

  // El resumen trae el evento ya reducido a dinero; el detalle necesita el
  // documento completo (desglose, proveedores asignados, planner).
  const eventosPorId = useMemo(() => {
    const out = {};
    (events || []).forEach((e) => {
      if (e?.id) out[e.id] = e;
    });
    return out;
  }, [events]);

  if (clientesLoading && !cliente) {
    return <div className="p-8 text-center text-zinc-400">Cargando...</div>;
  }
  if (!cliente) {
    return (
      <div className="p-8 text-center">
        <div className="text-zinc-500 mb-4">Cliente no encontrado.</div>
        <Button outline onClick={() => navigate("/dashboard/directorio?tipo=clientes")}>
          <ChevronLeftIcon className="h-4 w-4 mr-1" /> Directorio
        </Button>
      </div>
    );
  }

  const empezarEdicion = () => {
    setForm({
      clientType: cliente.clientType === "moral" ? "moral" : "fisica",
      nombreEmpresa: cliente.nombreEmpresa || "",
      nombre: cliente.nombre || "",
      apellido: cliente.apellido || "",
      correo: cliente.correo || "",
      telefono: cliente.telefono || "",
      rfc: cliente.rfc || "",
      notas: cliente.notas || "",
    });
    setEditando(true);
  };

  // ── Contactos ──
  // Toda escritura pasa por patchContactos: deja UN principal y espeja sus
  // datos en los campos inline, que es lo que siguen leyendo el picker, los
  // eventos y la búsqueda de coincidencias.
  const contactos = contactosDeCliente(cliente);

  const escribirContactos = async (lista, principalId, accion) => {
    try {
      await actualizarCliente({
        venueId: selectedVenueId,
        clienteId: id,
        datos: { ...cliente, ...patchContactos(lista, { principalId }) },
        user,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.CLIENTE_EDIT,
          entity: "cliente",
          entityId: id,
          entityName: nombreCliente(cliente),
          details: { contactos: accion },
        },
        userInfo,
        user
      );
    } catch (err) {
      showAlert(`No se pudo actualizar los contactos. [${err.message}]`, "error");
    }
  };

  const agregarContacto = () => {
    if (!nuevoContacto) return;
    if (nombreContacto(nuevoContacto) === "Contacto") {
      showAlert("Ponle al menos un nombre, correo o teléfono.", "error");
      return;
    }
    const principal = contactos.find((c) => c.principal);
    escribirContactos([...contactos, nuevoContacto], principal?.id, "agregar");
    setNuevoContacto(null);
  };

  const quitarContacto = (contactoId) =>
    escribirContactos(
      contactos.filter((c) => c.id !== contactoId),
      contactos.find((c) => c.principal && c.id !== contactoId)?.id,
      "quitar"
    );

  const hacerPrincipal = (contactoId) =>
    escribirContactos(contactos, contactoId, "principal");

  const guardar = async () => {
    setSaving(true);
    try {
      await actualizarCliente({ venueId: selectedVenueId, clienteId: id, datos: form, user });
      logBitacora(
        selectedVenueId,
        { action: BITACORA_ACTIONS.CLIENTE_EDIT, entity: "cliente", entityId: id, entityName: nombreCliente(form), details: {} },
        userInfo,
        user
      );
      setEditando(false);
      showAlert("Cliente actualizado", "success");
    } catch (err) {
      showAlert(err.message || "No se pudo guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleArchivo = async () => {
    const archivar = !cliente.archivedAt;
    try {
      await archivarCliente({ venueId: selectedVenueId, clienteId: id, user, archivar });
      logBitacora(
        selectedVenueId,
        {
          action: archivar ? BITACORA_ACTIONS.CLIENTE_ARCHIVE : BITACORA_ACTIONS.CLIENTE_UNARCHIVE,
          entity: "cliente",
          entityId: id,
          entityName: nombreCliente(cliente),
          details: {},
        },
        userInfo,
        user
      );
      showAlert(archivar ? "Cliente archivado" : "Cliente desarchivado", "success");
    } catch (err) {
      showAlert(err.message || "No se pudo actualizar", "error");
    }
  };

  const ligar = async (ev) => {
    try {
      await ligarEventoACliente({
        venueId: selectedVenueId,
        eventId: ev.id,
        cliente,
        limpiarRol: ev.clienteId !== cliente.id,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_CLIENTE_LINK,
          entity: "event",
          entityId: ev.id,
          entityName: ev.nombre || "",
          details: { clienteId: id, clienteNombre: nombreCliente(cliente) },
        },
        userInfo,
        user
      );
      showAlert("Evento ligado al cliente", "success");
    } catch (err) {
      showAlert(err.message || "No se pudo ligar", "error");
    }
  };

  // Lead nuevo desde el cliente: abre el diálogo de crear lead del tablero
  // con este cliente ya puesto. El lead existe hasta que el owner lo guarda
  // con nombre: antes se creaba un documento vacío en el tablero.
  const nuevoLead = () => {
    navigate(`/dashboard/leads?nuevo=${encodeURIComponent(id)}`);
  };

  const initial = nombreCliente(cliente).trim().charAt(0).toUpperCase();

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={() => navigate(backTarget.to)}
        className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 mb-3"
      >
        <ChevronLeftIcon className="h-4 w-4 mr-1" /> {backTarget.label}
      </button>

      {/* Identidad */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden mb-4">
        <div className="p-5 sm:p-6 flex items-start gap-4 flex-wrap">
          <div
            className="shrink-0 h-14 w-14 rounded-xl flex items-center justify-center text-2xl font-bold text-white"
            style={{ backgroundColor: ACCENT }}
            aria-hidden="true"
          >
            {initial || <UserIcon className="h-6 w-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Heading level={2} className="break-words">{nombreCliente(cliente)}</Heading>
              {cliente.clientType === "moral" && <Badge color="zinc">Persona moral</Badge>}
              {cliente.archivedAt && <Badge color="zinc">Archivado</Badge>}
            </div>
            <div className="mt-1 text-sm text-zinc-500">
              {[contactoCliente(cliente), cliente.correo, cliente.telefono].filter(Boolean).join(" · ") || "Sin datos de contacto"}
            </div>
            {cliente.rfc && <div className="text-xs text-zinc-400 mt-0.5">RFC {cliente.rfc}</div>}
          </div>
          <div className="flex items-center gap-2">
            {!cliente.archivedAt && !soloLectura && (
              <button
                type="button"
                onClick={nuevoLead}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
                title="Crear un lead con los datos de este cliente"
              >
                <LightBulbIcon className="h-4 w-4" />
                Nuevo lead
              </button>
            )}
            {!soloLectura && (
            <button
              type="button"
              onClick={toggleArchivo}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition"
              title={cliente.archivedAt ? "Desarchivar" : "Archivar"}
            >
              {cliente.archivedAt ? <ArchiveBoxArrowDownIcon className="h-4 w-4 rotate-180" /> : <ArchiveBoxIcon className="h-4 w-4" />}
            </button>
            )}
          </div>
        </div>
        {/* Cómo nos ha ido con este cliente, de un vistazo: lo que vale su
            historia, lo que ha pagado, lo que debe, lo que nos costó y lo que
            dejó. Es lo que se quiere ver cuando vuelve a cotizar. */}
        {(() => {
          // Seis ceros no dicen nada. Solo hay dinero que mostrar cuando
          // hay eventos vivos; costos y utilidad, cuando algún evento ya
          // tiene resultados. Si todo lo que hay está cancelado, se dice en
          // una línea, con lo que sí pagó.
          const vivos = resumen.eventos.length - resumen.cancelados;
          const soloCancelados = resumen.eventos.length > 0 && vivos === 0;
          const kpis = [
            {
              label: "Eventos",
              value: String(resumen.eventos.length + participa.length),
              hint: participa.length > 0 ? `${participa.length} donde participa` : null,
            },
            { label: "Leads", value: String(leadsDelCliente.length) },
            ...(vivos > 0
              ? [
                  { label: "Valor total", value: fmtMoney(resumen.valorTotal) },
                  { label: "Pagado", value: fmtMoney(resumen.pagado), color: "text-emerald-600" },
                  { label: "Debe", value: fmtMoney(resumen.debe), color: resumen.debe > 0 ? "text-amber-600" : "" },
                ]
              : []),
            ...(vivos > 0 && resumen.conResultados > 0
              ? [
                  { label: "Costos", value: fmtMoney(resumen.costosTotal) },
                  {
                    label: "Utilidad",
                    value: fmtMoney(resumen.utilidad),
                    color: resumen.utilidad < 0 ? "text-red-600" : "text-emerald-600",
                    hint: resumen.margen !== null ? `Margen ${Math.round(resumen.margen * 100)}%` : null,
                  },
                ]
              : []),
          ];
          return (
            <div className="border-t border-zinc-100 dark:border-zinc-700/40">
              <div className="flex flex-wrap divide-x divide-zinc-100 dark:divide-zinc-700/40 text-center">
                {kpis.map((k) => (
                  <div key={k.label} className="flex-1 min-w-[110px] px-3 py-3">
                    <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">{k.label}</div>
                    <div className={`mt-0.5 text-sm font-bold text-zinc-900 dark:text-white ${k.color || ""}`}>{k.value}</div>
                    {k.hint && <div className="text-[11px] text-zinc-400">{k.hint}</div>}
                  </div>
                ))}
              </div>
              {soloCancelados && (
                <div className="px-4 pb-3 text-xs text-zinc-500">
                  {resumen.cancelados === 1 ? "Su único evento está cancelado" : `Sus ${resumen.cancelados} eventos están cancelados`}
                  {resumen.pagado > 0 ? ` · ${fmtMoney(resumen.pagado)} pagados` : ""}.
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div className="space-y-4">
        {/* Datos */}
        <Card
          title="Datos del cliente"
          action={
            soloLectura ? null : editando ? (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setEditando(false)} className="text-xs text-zinc-500 hover:text-zinc-700">Cancelar</button>
                <button
                  type="button"
                  onClick={guardar}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={empezarEdicion}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition flex items-center gap-1.5"
              >
                <PencilSquareIcon className="h-3.5 w-3.5" />
                Editar
              </button>
            )
          }
        >
          {editando ? (
            <ClienteFormFields form={form} setForm={setForm} />
          ) : (
            (() => {
              // Solo lo que tiene valor: cuatro renglones con guion dicen
              // menos que una línea con lo que falta. "Persona física" no
              // se anuncia; "Persona moral" sí, porque cambia la lectura.
              const esMoral = cliente.clientType === "moral";
              const filas = [
                ...(esMoral ? [["Empresa", cliente.nombreEmpresa]] : []),
                [esMoral ? "Contacto" : "Nombre", [cliente.nombre, cliente.apellido].filter(Boolean).join(" ")],
                ["Correo", cliente.correo],
                ["Teléfono", cliente.telefono],
                ["RFC", cliente.rfc],
                ["Notas", cliente.notas],
              ];
              const conValor = filas.filter(([, v]) => !!(v && String(v).trim()));
              const faltan = filas
                .filter(([k, v]) => !(v && String(v).trim()) && k !== "Notas")
                .map(([k]) => (k === "RFC" ? k : k.toLowerCase()));
              return (
                <>
                  {esMoral && (
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">Persona moral</div>
                  )}
                  {conValor.length > 0 && (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      {conValor.map(([k, v]) => (
                        <div key={k} className={k === "Notas" ? "sm:col-span-2" : undefined}>
                          <dt className="text-xs uppercase tracking-wide font-semibold text-zinc-500">{k}</dt>
                          <dd className="mt-0.5 text-zinc-900 dark:text-zinc-100 break-words whitespace-pre-wrap">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {faltan.length > 0 && (
                    <p className={`text-xs text-zinc-400 ${conValor.length > 0 ? "mt-3" : ""}`}>
                      Sin {faltan.join(", ")}.
                      {!soloLectura && (
                        <>
                          {" "}
                          <button type="button" onClick={empezarEdicion} className="font-medium hover:underline" style={{ color: ACCENT }}>
                            Completar
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </>
              );
            })()
          )}
        </Card>

        {/* Contactos: con quién hablas en este cliente. Es cosa de empresas
            (la cuenta es la empresa, las personas son contactos). Una persona
            física ES su contacto: la tarjeta sería un espejo de los datos de
            arriba, así que no se muestra, salvo que ya tenga varios. */}
        {(cliente.clientType === "moral" || contactos.length > 1 || !!nuevoContacto) && (
        <Card
          title="Contactos"
          action={
            !soloLectura && (
              <button
                type="button"
                onClick={() => setNuevoContacto({ ...CONTACTO_VACIO, id: nuevoIdContacto() })}
                className="text-xs font-semibold hover:underline"
                style={{ color: ACCENT }}
              >
                Agregar contacto
              </button>
            )
          }
        >
          {contactos.length === 0 && !nuevoContacto ? (
            <p className="text-sm text-zinc-400 italic">
              Este cliente no tiene datos de contacto todavía.
            </p>
          ) : (
            <div className="space-y-2">
              {contactos.map((c) => (
                <div
                  key={c.id}
                  className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 px-3 py-2 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-900 dark:text-white">
                        {nombreContacto(c)}
                      </span>
                      {c.principal && <Badge color="green">Principal</Badge>}
                      {c.puesto && (
                        <span className="text-xs text-zinc-500">{c.puesto}</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 break-words">
                      {[c.correo, c.telefono].filter(Boolean).join(" · ") ||
                        "Sin correo ni teléfono"}
                    </div>
                  </div>
                  {!soloLectura && (
                    <div className="flex shrink-0 items-center gap-2 text-xs">
                      {!c.principal && (
                        <button
                          type="button"
                          onClick={() => hacerPrincipal(c.id)}
                          className="text-zinc-400 hover:text-zinc-700"
                          title="Los datos del principal son los que se usan al crear un evento"
                        >
                          Hacer principal
                        </button>
                      )}
                      {contactos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => quitarContacto(c.id)}
                          className="text-zinc-400 hover:text-red-500"
                          title="Quitar contacto"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {nuevoContacto && (
                <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-600 p-3 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      ["nombre", "Nombre"],
                      ["apellido", "Apellido"],
                      ["puesto", "Puesto (opcional)"],
                      ["correo", "Correo"],
                      ["telefono", "Teléfono"],
                    ].map(([campo, label]) => (
                      <div key={campo}>
                        <label className="block text-xs font-medium text-zinc-500 mb-1">
                          {label}
                        </label>
                        <input
                          type="text"
                          value={nuevoContacto[campo]}
                          onChange={(e) =>
                            setNuevoContacto((f) => ({ ...f, [campo]: e.target.value }))
                          }
                          className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setNuevoContacto(null)}
                      className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-700"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={agregarContacto}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ backgroundColor: ACCENT }}
                    >
                      Agregar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
        )}

        {/* Eventos */}
        <Card title="Eventos">
          {filasEventos.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">Todavía no tiene eventos ligados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-zinc-400">
                    <th className="px-2 py-1 text-left font-medium">Evento</th>
                    <th className="px-2 py-1 text-left font-medium">Fecha</th>
                    <th className="px-2 py-1 text-right font-medium">Valor</th>
                    <th className="px-2 py-1 text-right font-medium">Pagado</th>
                    <th className="px-2 py-1 text-right font-medium">Saldo</th>
                    <th className="px-2 py-1 text-right font-medium">Costos</th>
                    <th className="px-2 py-1 text-right font-medium">Utilidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {filasEventos.map((e) => {
                    const abierto = eventoAbierto === e.eventId;
                    return (
                    <React.Fragment key={e.eventId}>
                    <tr className={abierto ? "bg-zinc-50 dark:bg-zinc-800/60" : e.participa ? "text-zinc-500" : undefined}>
                      <td className="px-2 py-2">
                        <div className="flex items-start gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEventoAbierto(abierto ? null : e.eventId)}
                            aria-expanded={abierto}
                            aria-label={abierto ? "Cerrar detalle del evento" : "Ver conceptos, costos y proveedores"}
                            title={abierto ? "Cerrar detalle" : "Ver conceptos, costos y proveedores"}
                            className="mt-0.5 shrink-0 rounded text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                          >
                            <ChevronRightIcon className={`h-4 w-4 transition-transform ${abierto ? "rotate-90" : ""}`} />
                          </button>
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/eventos/${e.eventId}`, { state: { from: "cliente", clienteId: id } })}
                              className="font-medium text-zinc-900 dark:text-zinc-100 hover:underline text-left"
                            >
                              {e.nombre}
                            </button>
                            {e.cancelado && <Badge color="red" className="ml-2">Cancelado</Badge>}
                            {/* Participa con un rol: el evento es de otra ficha
                                y el dinero cuenta allá, no aquí. */}
                            {e.participa && (
                              <Badge color="zinc" className="ml-2" title="Participa en el evento de otro cliente; el valor y los pagos cuentan en el cliente principal">
                                {e.rol || "Participa"}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-zinc-500">
                        <span className="inline-flex items-center gap-1"><CalendarDaysIcon className="h-3.5 w-3.5" />{fmtDia(e.startTime, calendarTimezone)}</span>
                      </td>
                      {e.participa ? (
                        <>
                          <td className="px-2 py-2 text-right whitespace-nowrap text-zinc-400" title="Cuenta en el cliente principal del evento">
                            {e.cancelado || !e.valor ? "—" : fmtMoney(e.valor)}
                          </td>
                          <td className="px-2 py-2 text-right text-zinc-300">—</td>
                          <td className="px-2 py-2 text-right text-zinc-300">—</td>
                          <td className="px-2 py-2 text-right text-zinc-300">—</td>
                          <td className="px-2 py-2 text-right text-zinc-300">—</td>
                        </>
                      ) : (
                      <>
                      <td className="px-2 py-2 text-right whitespace-nowrap">{e.cancelado ? "—" : fmtMoney(e.valor)}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-600">{e.pagado > 0 ? fmtMoney(e.pagado) : "—"}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {e.saldo > 0 ? <span className="text-amber-600 font-medium">{fmtMoney(e.saldo)}</span> : "—"}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                        {e.costos > 0 ? fmtMoney(e.costos) : "—"}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {e.utilidad === null ? (
                          <span className="text-zinc-300">—</span>
                        ) : (
                          <span
                            className={`font-medium ${e.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}
                            title={e.margen !== null ? `Margen ${Math.round(e.margen * 100)}%` : undefined}
                          >
                            {fmtMoney(e.utilidad)}
                          </span>
                        )}
                      </td>
                      </>
                      )}
                    </tr>
                    {abierto && (
                      <tr className="bg-zinc-50 dark:bg-zinc-800/60">
                        <td colSpan={7} className="px-2 pb-4 pt-0">
                          <ClienteEventoDetalle
                            event={eventosPorId[e.eventId]}
                            costos={costos}
                            proveedores={proveedores}
                            cancelado={e.cancelado}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
                {resumen.eventos.length > 1 && (
                  <tfoot>
                    <tr className="border-t border-zinc-200 dark:border-zinc-700/60 text-xs font-semibold">
                      <td className="px-2 py-2 text-zinc-500" colSpan={2}>
                        Total · {resumen.eventos.length} eventos
                        {participa.length > 0 ? " como cliente principal" : ""}
                        {resumen.cancelados > 0 && (
                          <span className="font-normal text-zinc-400"> · {resumen.cancelados} cancelado{resumen.cancelados === 1 ? "" : "s"} fuera del margen</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-zinc-900 dark:text-white">{fmtMoney(resumen.valorTotal)}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-600">{fmtMoney(resumen.pagado)}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {resumen.debe > 0 ? <span className="text-amber-600">{fmtMoney(resumen.debe)}</span> : "—"}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">{fmtMoney(resumen.costosTotal)}</td>
                      <td className={`px-2 py-2 text-right whitespace-nowrap ${resumen.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}>
                        {fmtMoney(resumen.utilidad)}
                        {resumen.margen !== null && (
                          <span className="ml-1 font-normal text-zinc-400">({Math.round(resumen.margen * 100)}%)</span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </Card>

        {leadsDelCliente.length > 0 && (
          <Card title="Leads">
            <p className="text-xs text-zinc-500 mb-3">
              Cada vez que esta persona ha pedido cotización, en qué quedó y si se volvió evento.
            </p>
            <div className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {leadsDelCliente.map((f) => {
                const cotizaciones = (f.cotizacionHistory || []).length;
                const estado = String(f.status || "pendiente");
                return (
                  <div key={f.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/leads/${f.id}`)}
                        className="text-sm font-medium text-zinc-900 dark:text-white hover:underline truncate text-left"
                      >
                        {f.topic || f.name || "Lead"}
                      </button>
                      <div className="text-xs text-zinc-500">
                        {fmtDate(f.createdAt, calendarTimezone)}
                        {f.origin ? ` · ${f.origin}` : ""}
                        {cotizaciones > 0 && ` · ${cotizaciones} ${cotizaciones === 1 ? "cotización" : "cotizaciones"}`}
                      </div>
                    </div>
                    {f.convertedToEventId ? (
                      <Badge color="emerald">Convertido en evento</Badge>
                    ) : (
                      <Badge color={estado === "perdido" ? "red" : "zinc"}>
                        {etiquetaEstadoLead(estado, columnasLeads)}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Eventos viejos que parecen suyos */}
        {sinLigar.length > 0 && !soloLectura && (
          <Card title="Eventos que parecen de este cliente">
            <p className="text-xs text-zinc-500 mb-3">
              Coinciden por correo o teléfono pero no están ligados a este cliente (se capturaron antes de que existiera). Lígalos para que cuenten en su historial.
            </p>
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {sinLigar.map((ev) => (
                <li key={ev.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{ev.nombre || "Sin nombre"}</div>
                    <div className="text-xs text-zinc-500">{fmtDate(ev.startTime, calendarTimezone)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => ligar(ev)}
                    className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                    style={{ color: ACCENT }}
                  >
                    <LinkIcon className="h-3.5 w-3.5" /> Ligar
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientePage;
